import OpenAI from 'openai';
import React, { useState } from 'react';
import TextInput from './TextInput';
import TokenDisplay from './TokenDisplay';


export interface TokenInfo {
    token: string;
    probability: number;
    topAlternatives: Array<{
        token: string;
        probability: number;
    }>;
}

const Chat: React.FC<{ temperature: number, maxTokens: number }> = ({ temperature, maxTokens }) => {

    const [inputText, setInputText] = useState('');
    const [tokens, setTokens] = useState<TokenInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setTokens([]);

        try {
            const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

            if (!apiKey) {
                throw new Error('OpenAI API key not found. Please set REACT_APP_OPENAI_API_KEY in your .env file');
            }

            const openai = new OpenAI({
                apiKey: apiKey,
                dangerouslyAllowBrowser: true // Note: In production, use a backend proxy
            });

            const response = await openai.completions.create({
                model: 'gpt-3.5-turbo-instruct',
                prompt: inputText,
                temperature: temperature,
                max_tokens: maxTokens,
                logprobs: 10, // Get top 10 alternative tokens
            });

            const choice = response.choices[0];

            if (choice.logprobs) {
                const tokenInfos: TokenInfo[] = [];
                const tokensArray = choice.logprobs.tokens || [];
                const tokenLogprobs = choice.logprobs.token_logprobs || [];
                const topLogprobs = choice.logprobs.top_logprobs || [];

                for (let i = 0; i < tokensArray.length; i++) {
                    const token = tokensArray[i];
                    const logprob = tokenLogprobs[i];
                    const probability = logprob !== null ? Math.exp(logprob) : 0;

                    const alternatives: Array<{ token: string; probability: number }> = [];

                    if (topLogprobs[i]) {
                        const topLogprobsObj = topLogprobs[i];
                        for (const [altToken, altLogprob] of Object.entries(topLogprobsObj)) {
                            alternatives.push({
                                token: altToken,
                                probability: Math.exp(altLogprob as number)
                            });
                        }
                    }

                    // Sort alternatives by probability
                    alternatives.sort((a, b) => b.probability - a.probability);

                    tokenInfos.push({
                        token,
                        probability,
                        topAlternatives: alternatives.slice(0, 10)
                    });
                }

                setTokens(tokenInfos);
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred while generating completion');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <TextInput
                value={inputText}
                onChange={setInputText}
                onGenerate={handleGenerate}
                loading={loading}
                tokens={tokens}
            />
            {error && <div className="error alert alert-danger fade show" role="alert">{error}</div>}
            {loading && (
                <div className="loading-spinner">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            {/* {tokens.length > 0 && <TokenDisplay tokens={tokens} />} */}
        </div>
    );
}

export default Chat;